import {AfterViewInit, Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import Quill from 'quill';
import QuillNamespace from 'quill';
import {defaultEditorConfig} from '../../common/html-editor.config';
import {LoadingService} from '../services/loading.service';
import {MessagesService} from '../services/messages.service';
import {FileUploadService} from '../../services/file-upload.service';
import {TenantService} from '../../services/tenant.service';
import {catchError, concatMap, last, tap} from 'rxjs/operators';
import {throwError} from 'rxjs';
// this is the module that allows for image resizing
import ImageResize from 'quill-image-resize-module';

// this module allows to drag and drop images
// import {ImageDrop} from 'quill-image-drop-module';

import AttachmentHandler from './handlers/attachment/AttachmentHandler';
import AttachmentBlot from './blots/attachment/AttachmentBlot';
import {AppState} from '../../store';
import {select, Store} from '@ngrx/store';
import {isAdmin} from '../../store/selectors';
import {UrlBuilderService} from '../../services/url-builder.service';
import HTMLEmbedHandler from './handlers/html-embed/HTMLEmbedHandler';
import HTMLEmbedBlot from './blots/html-embed/HTMLEmbedBlot';

import EmojiBlot from "./blots/emoji/EmojiBlot";
import EmojiHandler from "./handlers/emoji/EmojiHandler";

import {NgStyle} from '@angular/common';
import { createFirestoreId } from '../../common/firestore-utils';
import { Firestore } from '@angular/fire/firestore';
let QuillGlobal: any = QuillNamespace;

// https://github.com/Etoile984816138/quill-image-resize-module
QuillGlobal.register("modules/imageResize", ImageResize);

// https://github.com/kensnyder/quill-image-drop-module
// QuillGlobal.register("modules/imageDrop", ImageDrop);

type OnUploadCompleted = (url: string) => void;
Quill.register("blots/attachment", AttachmentBlot);
Quill.register("blots/htmlembed", HTMLEmbedBlot);
Quill.register("modules/attachment", AttachmentHandler);
Quill.register("modules/htmlembed", HTMLEmbedHandler);
Quill.register("modules/emoji", EmojiHandler);
Quill.register("blots/emoji", EmojiBlot);

/**
 *
 * This is the platform rich text component, based on Quill.js.
 *
 * besides the core capabilities of Quill, it allows for:
 *
 * - resizing images via the package quill-image-resize-module
 *
 * - drag and drop images via the package quill-image-drop-module
 *
 * - image upload to Google cloud storage using a custom module
 *
 */

@Component({
    selector: "rich-text-editor",
    templateUrl: "rich-text-editor.component.html",
    styleUrls: ["rich-text-editor.component.scss"],
    standalone: true,
    imports: [NgStyle],
})
export class RichTextEditorComponent
  implements OnInit, AfterViewInit, OnChanges
{
  getContent() {
    throw new Error("Method not implemented.");
  }
  editorId: string;

  quill: Quill;

  @Input()
  placeholder: string;

  @Input()
  modules: any;

  @Input()
  height: string;

  @Input()
  html: string;

  @Input()
  fixedToTop: boolean;
  @HostBinding('class') class

  isAdmin = false;

  @Output()
  checkEmailLength: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  contentChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  onRichTextViewInit: EventEmitter<void> = new EventEmitter<void>();

  nativeElement: any;

  constructor(    private loading: LoadingService,
    private messages: MessagesService,
    private upload: FileUploadService,
    private tenant: TenantService,
    private store: Store<AppState>,
    private ub: UrlBuilderService,
    private firestore: Firestore
  ) {}

  ngOnInit() {
    this.editorId = "editor-" + createFirestoreId(this.firestore);

    this.store
      .pipe(select(isAdmin))
      .subscribe((isAdmin) => (this.isAdmin = isAdmin));

    this.setToolbarIcons();
    this.class = this.getFixedToTopClass()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["html"] && !changes["html"]?.firstChange) {
      const html = changes["html"].currentValue;

      if (html) {
        // resets the editor to empty
        this.quill.setContents([]);

        // initialize the content to exactly what we have in our database
        //this.quill.clipboard.dangerouslyPasteHTML(0, html);
        this.quill.root.innerHTML = html;
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initQuill();
    });
  }

  initQuill() {
    const editorSelector = `#${this.editorId}`;

    // start up Quill
    this.quill = new Quill(editorSelector, {
      bounds: editorSelector,
      modules: {
        ...(this.modules ?? defaultEditorConfig(false)),
        attachment: {
          upload: (file: File, onUploadCompleted: OnUploadCompleted) => {
            this.saveToCloudStorage(file, onUploadCompleted);
          },
          icons: {
            download: this.ub.buildImageUrl("/images/icons/DownloadSimple.svg"),
            trash: this.ub.buildImageUrl("/images/icons/Trash.svg"),
            file: this.ub.buildImageUrl("/images/icons/File.svg"),
            paperClip: this.ub.buildImageUrl(
              "/images/icons/toolbar/PaperClip.svg"
            ),
          },
        },
        htmlembed: {
          icons: {
            embed: this.ub.buildImageUrl("/images/icons/toolbar/Embed.svg"),
          },
        },
        emoji: {
          icons: {
            emoji: this.ub.buildImageUrl("/images/icons/toolbar/Emoji.svg"),
          }
        }
      },
      placeholder: this.placeholder ?? "Enter your text here",
      theme: "snow",
    });

    // custom module for uploading images to Google cloud
    this.quill.getModule("toolbar").addHandler("image", () => {
      this.startImageUpload();
    });

    if (this.html) {
      // this.quill.setContents(this.quill.clipboard.convert(this.html));

      this.quill.root.innerHTML = this.html;
    }

    this.quill.on("text-change", (delta: any, oldDelta: any, source: string) => {
      if (source == 'user') {
        this.contentChange.emit(this.quill.root.textContent);
      }
    })

    this.onRichTextViewInit.emit()
  }

  startImageUpload() {
    const input = document.createElement("input");

    input.setAttribute("type", "file");
    input.click();

    // Listen upload local image and save to server
    input.onchange = () => {
      const file = input.files[0];

      // file type is only image.
      if (/^image\//.test(file.type)) {
        this.saveToCloudStorage(file, (url) => this.insertImageToEditor(url));
      } else {
        this.messages.error(
          "This file is not an image, please select another file."
        );
      }
    };
  }

  saveToCloudStorage(file: File, onUpload: OnUploadCompleted) {
    const storageFileName = this.upload.generateUrlSafeUploadFileName(file);

    const storagePath = `${this.tenant.id}/richTextImages`;

    const fileUploadTask$ = this.upload.uploadFileWithNewName(
      file,
      storagePath,
      storageFileName,
      file.name
    );

    const upload$ = this.upload.uploadTaskToObservable(fileUploadTask$).pipe(
      last(),
      catchError((err) => {
        console.log("Error uploading File, reason: ", err);
        this.messages.error(
          `Error uploading file, the file ${file.name} cannot exceed 500K.`
        );
        return throwError(err);
      }),
      concatMap(() =>
        this.upload.getDownloadUrl(`${storagePath}/${storageFileName}`)
      ),
      tap((url) => onUpload(url))
    );

    this.loading.showLoaderUntilCompleted(upload$).subscribe();
  }

  insertImageToEditor(url: string) {
    const range = this.quill.getSelection();
    this.quill.insertEmbed(range.index, "image", url);
  }

  getHtml(): string {
    const container = document.getElementById(this.editorId);

    if (!container) {
      return null;
    }

    const results = container.getElementsByClassName("ql-editor");

    if (results.length != 1) {
      return null;
    }

    const editorDiv = results[0];

    return editorDiv.innerHTML;
  }

  setToolbarIcons() {
    const icons = Quill.import("ui/icons");

    [
      { icon: "TextBolder", name: "bold" },
      { icon: "TextItalic", name: "italic" },
      { icon: "TextUnderline", name: "underline" },
      { icon: "Quotes", name: "blockquote" },
      { icon: "TextStrikethrough", name: "strike" },
      { icon: "ListNumbers", name: "list", level: "ordered" },
      { icon: "ListBullets", name: "list", level: "bullet" },
      { icon: "TextOutdent", name: "indent", level: "-1" },
      { icon: "TextIndent", name: "indent", level: "+1" },

      { icon: "TextSize", name: "color" },

      { icon: "TextAlignLeft", name: "align", level: "" },
      { icon: "Justify", name: "align", level: "justify" },
      { icon: "Right", name: "align", level: "right" },
      { icon: "Center", name: "align", level: "center" },

      { icon: "Link", name: "link" },
      { icon: "Image", name: "image" },
      { icon: "FilmStrip", name: "video" },
      { icon: "Code", name: "code-block" },
    ].forEach(({ name, icon, level = undefined }) => {
      const iconPath = this.ub.buildImageUrl(
        `/images/icons/toolbar/${icon}.svg`
      );
      if (level !== undefined) {
        if (level === "") {
          icons[name][""] = this.getImg(iconPath);
        } else {
          icons[name][level] = this.getImg(iconPath);
        }
      } else icons[name] = this.getImg(iconPath);
    });
  }

  getImg(path: string) {
    return `<img src="${path}" />`;
  }

  getFixedToTopClass (): string {
    return this.fixedToTop ? 'fixedToTop60' : 'fixedToTop0'
  }

}
